import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const checkpointRecoveryComponent: ComponentDef = {
  id: 'checkpoint-recovery',
  name: 'Checkpoint Recovery',
  description: 'Save and resume training state from checkpoints to handle failures.',
  category: 'training',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_layers', label: 'Layers', type: 'number', default: 12, min: 1, max: 256, step: 1 },
    {
      name: 'variant',
      label: 'Strategy',
      type: 'select',
      default: 'strategy_comparison',
      options: [
        { label: 'Strategy Comparison', value: 'strategy_comparison' },
        { label: 'Sharded State Dict', value: 'sharded' },
        { label: 'Fault Tolerance', value: 'fault_tolerance' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const n_layers = Number(params.n_layers);
    const variant = String(params.variant || 'strategy_comparison');

    const g = new GraphBuilder();

    if (variant === 'strategy_comparison') {
      // Strategy Comparison: show full-state vs distributed checkpoint side by side
      g.input('model', [n_layers, d_model]);

      // Full-state checkpoint (single file)
      g.cache('full_ckpt', 'model', [n_layers, d_model]);
      g.scale('full_optimizer', 'full_ckpt', 'Optimizer State');
      g.scale('full_rng', 'full_optimizer', 'RNG State');
      g.concat('full_state', ['full_ckpt', 'full_optimizer', 'full_rng'], [3]);

      // Distributed checkpoint (per-rank shards)
      g.split('shards', 'model', [d_model / 2], 'Split across ranks');
      g.cache('dist_ckpt', 'shards', [n_layers, d_model]);
      g.scale('dist_optimizer', 'dist_ckpt', 'Sharded Optimizer');
      g.concat('dist_state', ['dist_ckpt', 'dist_optimizer'], [2]);

      // Compare resume paths
      g.scale('resume_full', 'full_state', 'Resume (full)');
      g.scale('resume_dist', 'dist_state', 'Resume (distributed)');
      g.add('comparison', 'resume_full', 'resume_dist');
      g.output('out', 'comparison');

    } else if (variant === 'sharded') {
      // Sharded State Dict: each rank saves its own shard
      g.input('model', [n_layers, d_model]);

      // Split model across ranks
      g.split('rank_0', 'model', [d_model / 2], 'Rank 0 shard');
      g.split('rank_1', 'model', [d_model / 2], 'Rank 1 shard');

      // Each rank saves independently
      g.cache('shard_0', 'rank_0', [n_layers / 2, d_model]);
      g.cache('shard_1', 'rank_1', [n_layers / 2, d_model]);

      // Optimizer states are also sharded
      g.scale('opt_0', 'shard_0', 'Optimizer shard 0');
      g.scale('opt_1', 'shard_1', 'Optimizer shard 1');

      // Gather shards to reconstruct full state
      g.allgather('gather_model', 'shard_0', [d_model]);
      g.allgather('gather_opt', 'opt_0', [d_model]);
      g.concat('restored', ['gather_model', 'gather_opt'], [2]);
      g.scale('resume', 'restored', 'Resume Training');
      g.output('out', 'resume');

    } else {
      // Fault Tolerance: async checkpoint + failure detection + recovery
      g.input('model', [n_layers, d_model]);

      // Async checkpoint: copy to CPU buffer while GPU continues training
      g.cache('gpu_state', 'model', [n_layers, d_model]);
      g.scale('cpu_buffer', 'gpu_state', 'Async copy to CPU');
      g.cache('disk_ckpt', 'cpu_buffer', [n_layers, d_model]);

      // Optimizer + RNG state
      g.scale('optimizer', 'disk_ckpt', 'Optimizer State');
      g.scale('rng', 'optimizer', 'RNG State');
      g.concat('full_state', ['disk_ckpt', 'optimizer', 'rng'], [3]);

      // Failure detection and recovery path
      g.scale('heartbeat', 'full_state', 'Health Check');
      g.scale('detect_failure', 'heartbeat', 'Failure Detection');
      g.cache('recovery', 'detect_failure', [n_layers, d_model]);
      g.scale('resume', 'recovery', 'Resume from last good ckpt');
      g.output('out', 'resume');
    }

    return g.build();
  }
};
